import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { ArrowLeft, Plus, Trash2, Loader2, ClipboardList, CheckCircle2, Pencil, X, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";
import { SearchableSelect } from "./SearchableSelect";
import { Field } from "./shared";
import { ELEMENTS, fmtKg, TeneursFields, CompleterProduit } from "./BilanFertilisation";
import { UNITS, fertiProducts, productItems } from "./fertiProducts";
import CulturePicker from "@/components/CulturePicker";
import { useCultureLookup, normCulture, regionOfParcel } from "@/lib/cultures";

// Le cycle dépend de la culture (Anthony, 26/10/2026) : pas de règle unique
export const TYPES_CYCLE = [
  { value: "campagne", label: "Campagne (canne)", hint: "De coupe à coupe" },
  { value: "cycle", label: "Cycle de culture", hint: "De la préparation à la dernière récolte" },
  { value: "fond", label: "Fumure de fond", hint: "Verger : apports tous les 3 ou 4 ans" },
  { value: "entretien", label: "Entretien", hint: "Verger : entre deux fumures de fond" },
];
const STADES = {
  campagne: ["Après coupe", "Tallage", "Pleine croissance", "Avant fermeture"],
  cycle: ["Avant plantation", "Plantation", "Reprise", "Croissance", "Floraison", "Grossissement", "Entre deux récoltes"],
  fond: ["Avant plantation", "Plantation", "Fumure de fond"],
  entretien: ["Après récolte", "Floraison", "Grossissement des fruits", "Entretien"],
};
// Stades propres à une culture (sinon ceux du type de cycle)
const STADES_CULTURE = {
  "canne a sucre|plantation": ["Fond / plantation", "Tallage", "Pleine croissance", "Avant fermeture"],
  "ananas": ["Plantation", "Croissance végétative", "Avant induction florale", "Grossissement du fruit", "Rejets"],
  "banane": ["Plantation", "Croissance", "Floraison", "Remplissage des régimes", "Rejet suivant"],
  "maraichage": ["Fond / préparation", "Plantation", "Reprise", "Croissance", "Entre deux récoltes"],
};
// Cultures pérennes conduites comme un verger : fumure de fond ou entretien
const PERENNES = ["agrumes", "letchi", "mangue", "goyavier", "verger", "fruits tropicaux", "cafe", "the", "cacao", "cocotier", "palmiste", "palmier"];

/** Comment se conduit la culture : canne (campagne, plantation ou repousse), pérenne (fond ou entretien), sinon un cycle. */
export function profilCulture(name, ref) {
  const n = normCulture(name);
  if (!n) return "cycle";
  if ((ref?.famille && normCulture(ref.famille) === "canne a sucre") || n.startsWith("canne")) return "canne";
  if (PERENNES.includes(n) || PERENNES.includes(normCulture(ref?.libelle))) return "perenne";
  return "cycle";
}
const finCampagne = (debut) => {
  if (!debut) return "";
  const d = new Date(`${debut}T12:00:00`); d.setFullYear(d.getFullYear() + 1); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};
// Types de sol de la carte pédologique (Réunion : Raunet 1991 ; Mayotte) et sols fréquents ; saisie libre possible
const SOLS = ["Sol ferrallitique", "Sol brun andique", "Sol brun vertique", "Andosol", "Andosol perhydraté / Podzol",
  "Vertisol", "Sol alluvial", "Sol peu évolué (scories, lapillis)", "Ferralsol rouge tropical", "Sol brun tropical / Vertisol",
  "Andosol (volcanique récent)"];
const typeLabel = (v) => (TYPES_CYCLE.find((t) => t.value === v) || {}).label || v;
const cycleLabel = (f) => [typeLabel(f.type_cycle), f.conduite].filter(Boolean).join(" · ");
const frDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR") : "");
const today = () => new Date().toISOString().slice(0, 10);

async function jsonOrThrow(res, fallback) {
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : fallback);
  return d;
}

const inputCls = "h-8 px-2 rounded-md border border-black/10 bg-white text-xs";
const post = (url, body) => authFetch(`${API_BASE}${url}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}) });

/** Choix d'un modèle (les miens, ceux partagés, ceux de Véyèr CIEL) et date de début du cycle. */
function DepuisModele({ parcelId, ilotId, onDone, onCancel }) {
  const [data, setData] = useState(null);
  const [modeleId, setModeleId] = useState("");
  const [debut, setDebut] = useState(today());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    authFetch(`${API_BASE}/carnet/modeles-ferti`).then((r) => (r.ok ? r.json() : { modeles: [] })).then(setData).catch(() => setData({ modeles: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const modeles = data?.modeles || [];
  const sel = modeles.find((m) => m.modele_id === modeleId);
  const origin = { mine: "Mes modèles", shared: "Partagés avec moi", template: "Modèles Véyèr CIEL" };
  const appliquer = async () => {
    setBusy(true);
    try {
      await jsonOrThrow(await post(`/carnet/modeles-ferti/${modeleId}/appliquer`, { parcel_id: parcelId, ilot_id: ilotId || null, date_debut: debut }), "Application impossible");
      toast.success("Fiche créée depuis le modèle");
      onDone();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const supprimer = async () => {
    try {
      await jsonOrThrow(await authFetch(`${API_BASE}/carnet/modeles-ferti/${modeleId}`, { method: "DELETE" }), "Suppression impossible");
      toast.success("Modèle supprimé"); setModeleId(""); load();
    } catch (e) { toast.error(e.message); }
  };
  if (!data) return <Loader2 className="w-4 h-4 animate-spin" />;
  if (!modeles.length) return (
    <div className="text-[11px] space-y-1.5" style={{ color: "#4A4A4A" }}>
      <p>Aucun modèle pour l'instant. Une fiche s'enregistre comme modèle depuis « Enregistrer comme modèle ».</p>
      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={onCancel}>Retour</Button>
    </div>
  );
  return (
    <div className="space-y-2" data-testid="fiche-depuis-modele">
      <select value={modeleId} onChange={(e) => setModeleId(e.target.value)} className={`w-full ${inputCls}`} data-testid="modele-select">
        <option value="">Choisir un modèle…</option>
        {["mine", "shared", "template"].map((o) => modeles.some((m) => m.origin === o) && (
          <optgroup key={o} label={origin[o]}>
            {modeles.filter((m) => m.origin === o).map((m) => <option key={m.modele_id} value={m.modele_id}>{m.name}{m.culture ? ` · ${m.culture}` : ""}</option>)}
          </optgroup>
        ))}
      </select>
      {sel && (
        <p className="text-[10px]" style={{ color: "#7A7A7A" }}>
          {typeLabel(sel.type_cycle)} · {(sel.prevus || []).length} apport(s) prévu(s){sel.duree_jours ? ` · ${sel.duree_jours} jours` : ""}{sel.origin === "shared" ? ` · partagé par ${sel.owner_name || sel.owner_email}` : ""}
        </p>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[10px]" style={{ color: "#4A4A4A" }}>Début du cycle
          <input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} className={`block ${inputCls}`} data-testid="modele-debut" />
        </label>
        <Button size="sm" className="h-8 text-[11px] text-white" style={{ background: "#8B5E3C" }} disabled={!modeleId || !debut || busy} onClick={appliquer} data-testid="modele-appliquer">
          {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Créer la fiche
        </Button>
        <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={onCancel}>Annuler</Button>
        {sel?.can_edit && <button type="button" onClick={supprimer} className="ml-auto text-[10px]" style={{ color: "#DC2626" }} data-testid="modele-supprimer">Supprimer ce modèle</button>}
      </div>
    </div>
  );
}

/** Fiche ferti de l'îlot ou de la parcelle : bilan objectif / prévu / réalisé, apports prévus, fin de cycle, modèles. */
export function FicheFertiPanel({ parcelId, ilotId, onEdit, onRealise, reloadKey, onFiche }) {
  const [fiches, setFiches] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [bilan, setBilan] = useState(null);
  const [realising, setRealising] = useState(null); // { prevu_id, date, dose }
  const [panel, setPanel] = useState(null); // "cloture" | "suivant" | "modele" | "depuis-modele"
  const [recoltes, setRecoltes] = useState([]);
  const [suivantDebut, setSuivantDebut] = useState(today());
  const [modeleForm, setModeleForm] = useState({ name: "", shared: "", template: false });
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [vue, setVue] = useState("bilan"); // téléphone : onglet affiché

  const load = useCallback(async (keep) => {
    try {
      const res = await authFetch(`${API_BASE}/carnet/fiches-ferti?parcel_id=${parcelId}${ilotId ? `&ilot_id=${ilotId}` : ""}`);
      const list = res.ok ? await res.json() : [];
      setFiches(list);
      const cur = list.find((f) => f.fiche_id === keep) || list.find((f) => f.statut === "en_cours") || list[0];
      setSelectedId(cur?.fiche_id || null);
      // Le bilan « depuis le » s'efface seulement derrière une fiche en cours
      onFiche?.(list.find((f) => f.statut === "en_cours") || null);
      if (cur) {
        const b = await authFetch(`${API_BASE}/carnet/fiches-ferti/${cur.fiche_id}/bilan`);
        setBilan(b.ok ? await b.json() : null);
      } else setBilan(null);
    } catch (err) { console.error("[FicheFerti] chargement impossible :", err); setFiches([]); }
  }, [parcelId, ilotId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => {
    authFetch(`${API_BASE}/carnet/modeles-ferti`).then((r) => (r.ok ? r.json() : {})).then((d) => setIsAdmin(!!d.is_admin)).catch(() => {});
  }, []);

  if (fiches === null) return null;
  const fiche = fiches.find((f) => f.fiche_id === selectedId);
  const hasEnCours = fiches.some((f) => f.statut === "en_cours");

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); setPanel(null); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  if (!fiche || (panel === "depuis-modele" && !hasEnCours)) {
    return (
      <div className="mx-3 mt-3 rounded-xl border border-dashed p-3 space-y-2" style={{ borderColor: "#8B5E3C55" }} data-testid="fiche-ferti-vide">
        <div className="flex flex-wrap items-center gap-3">
          <ClipboardList className="w-5 h-5 shrink-0" style={{ color: "#8B5E3C" }} />
          <p className="flex-1 min-w-[180px] text-[11px]" style={{ color: "#4A4A4A" }}>
            {fiche ? "Nouvelle fiche ferti depuis un modèle." : `Pas encore de fiche ferti pour ${ilotId ? "cet îlot" : "cette parcelle"} : objectifs, apports prévus et suivi du cycle.`}
          </p>
          {panel !== "depuis-modele" && (
            <>
              <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => setPanel("depuis-modele")} data-testid="fiche-depuis-modele-btn">Depuis un modèle</Button>
              <Button size="sm" className="h-8 text-[11px] text-white" style={{ background: "#8B5E3C" }} onClick={() => onEdit({})} data-testid="fiche-creer">Créer la fiche ferti</Button>
            </>
          )}
        </div>
        {panel === "depuis-modele" && <DepuisModele parcelId={parcelId} ilotId={ilotId} onDone={() => { setPanel(null); load(); }} onCancel={() => setPanel(null)} />}
      </div>
    );
  }

  const units = bilan?.prevus || {};
  const termine = fiche.statut === "terminee";
  const recolteTotale = (fiche.recoltes || []).reduce((s, r) => s + (r.rendement_t_ha || 0), 0);
  const realiser = () => run(async () => {
    await jsonOrThrow(await post(`/carnet/fiches-ferti/${fiche.fiche_id}/prevus/${realising.prevu_id}/realiser`, { date: realising.date, dose: realising.dose || null }), "Enregistrement impossible");
    setRealising(null); await load(fiche.fiche_id); onRealise?.();
  }, "Apport réalisé, ajouté à la fertilisation");
  const cloturer = () => run(async () => {
    const rec = recoltes.filter((r) => r.date).map((r) => ({ date: r.date, rendement_t_ha: r.rendement === "" ? null : parseFloat(String(r.rendement).replace(",", ".")) }));
    await jsonOrThrow(await post(`/carnet/fiches-ferti/${fiche.fiche_id}/cloturer`, { recoltes: rec }), "Clôture impossible");
    await load(fiche.fiche_id);
  }, "Cycle clôturé");
  const rouvrir = () => run(async () => {
    await jsonOrThrow(await post(`/carnet/fiches-ferti/${fiche.fiche_id}/rouvrir`), "Réouverture impossible");
    await load(fiche.fiche_id);
  }, "Cycle rouvert");
  const suivant = () => run(async () => {
    const d = await jsonOrThrow(await post(`/carnet/fiches-ferti/${fiche.fiche_id}/cycle-suivant`, { date_debut: suivantDebut }), "Création impossible");
    await load(d.fiche_id);
  }, "Fiche du cycle suivant créée");
  const exporterPdf = () => run(async () => {
    const res = await authFetch(`${API_BASE}/carnet/fiches-ferti/${fiche.fiche_id}/pdf`);
    if (!res.ok) throw new Error("Export impossible");
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url; a.download = `fiche-ferti-${parcelId}-${fiche.date_debut}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  });
  const enregistrerModele = () => run(async () => {
    await jsonOrThrow(await post(`/carnet/fiches-ferti/${fiche.fiche_id}/modele`, {
      name: modeleForm.name, shared_with: modeleForm.shared.split(/[\s,;]+/).filter(Boolean), is_template: modeleForm.template,
    }), "Enregistrement impossible");
  }, "Modèle enregistré");

  const prevusList = fiche.prevus || [];
  const aFaire = prevusList.filter((p) => !p.realise_fertilisation_id).length;
  const th = "px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider";
  const td = "px-2 py-1.5 align-top";
  const sectionTitle = "text-[10px] font-bold uppercase tracking-wider";

  return (
    <div className="mx-3 mt-3 rounded-xl border bg-white overflow-hidden" style={{ borderColor: "#8B5E3C40" }} data-testid="fiche-ferti">
      {/* En-tête : titre, ligne d'informations, actions */}
      <div className="p-3 flex flex-wrap items-end gap-3 border-b-2" style={{ borderColor: "#1A1A1A" }}>
        <div className="flex-1 min-w-[220px] space-y-0.5">
          <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#8B5E3C" }}>
            Fiche ferti · {termine ? "cycle clôturé" : "cycle en cours"}
            {termine && <span className="ml-1.5 text-[9px] font-semibold px-1.5 py-0.5 rounded normal-case tracking-normal" style={{ background: "#E3EFE7", color: "#2D6A4F" }} data-testid="fiche-terminee">Cycle clôturé</span>}
          </p>
          <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{[fiche.culture, cycleLabel(fiche)].filter(Boolean).join(" · ")}</p>
          <p className="text-[11px] tabular-nums" style={{ color: "#4A4A4A" }}>
            {[`${frDate(fiche.date_debut)}${fiche.date_fin ? ` → ${frDate(fiche.date_fin)}` : ""}`,
              fiche.objectif_rendement_t_ha ? `objectif ${fiche.objectif_rendement_t_ha} t/ha` : null,
              fiche.recoltes_prevues ? `${fiche.recoltes_prevues} récolte(s) prévue(s)` : null,
              fiche.sol_type ? `sol : ${fiche.sol_type}` : null].filter(Boolean).join(" · ")}
          </p>
          {(fiche.objectifs_source || fiche.modele_name) && (
            <p className="text-[10px]" style={{ color: "#7A7A7A" }}>
              {[fiche.objectifs_source && `Objectifs : ${fiche.objectifs_source}`, fiche.modele_name && `créée depuis le modèle « ${fiche.modele_name} »`].filter(Boolean).join(" · ")}
            </p>
          )}
          {(fiche.recoltes || []).length > 0 && (
            <p className="text-[10px]" style={{ color: "#2D6A4F" }} data-testid="fiche-recoltes">
              Récolte(s) : {fiche.recoltes.map((r) => `${frDate(r.date)}${r.rendement_t_ha != null ? ` ${r.rendement_t_ha} t/ha` : ""}`).join(" · ")}
              {recolteTotale > 0 && ` · total ${recolteTotale.toLocaleString("fr-FR")} t/ha`}
              {recolteTotale > 0 && fiche.objectif_rendement_t_ha ? ` (${Math.round((recolteTotale / fiche.objectif_rendement_t_ha) * 100)} % de l'objectif)` : ""}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" className="h-8 text-[11px] gap-1" onClick={() => onEdit(fiche)} data-testid="fiche-modifier"><Pencil className="w-3 h-3" /> Modifier</Button>
          {!termine && <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => { setRecoltes((fiche.recoltes || []).length ? fiche.recoltes.map((r) => ({ date: r.date, rendement: r.rendement_t_ha ?? "" })) : [{ date: today(), rendement: "" }]); setPanel("cloture"); }} data-testid="fiche-cloturer">Clôturer le cycle</Button>}
          {termine && !hasEnCours && <Button size="sm" className="h-8 text-[11px] text-white" style={{ background: "#8B5E3C" }} onClick={() => { setSuivantDebut(fiche.date_fin && fiche.date_fin > today() ? fiche.date_fin : today()); setPanel("suivant"); }} data-testid="fiche-suivant">Préparer le cycle suivant</Button>}
          {termine && !hasEnCours && <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={rouvrir} data-testid="fiche-rouvrir">Rouvrir</Button>}
          <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => { setModeleForm({ name: `${fiche.culture || "Fiche"} · ${cycleLabel(fiche)}`, shared: "", template: false }); setPanel("modele"); }} data-testid="fiche-modele">Enregistrer comme modèle</Button>
          <Button size="sm" className="h-8 text-[11px] gap-1 text-white" style={{ background: "#1A1A1A" }} disabled={busy} onClick={exporterPdf} data-testid="fiche-pdf"><FileDown className="w-3 h-3" /> Exporter en PDF</Button>
        </div>
      </div>

      {(panel === "cloture" || panel === "suivant" || panel === "modele" || (panel === "depuis-modele" && hasEnCours)) && (
        <div className="p-3 border-b border-black/5" style={{ background: "#FAFAF7" }}>
          {panel === "cloture" && (
            <div className="space-y-2" data-testid="fiche-cloture">
              <p className="text-[11px] font-semibold">Récoltes du cycle</p>
              {recoltes.map((r, i) => (
                <div key={i} className="flex flex-wrap items-end gap-2">
                  <label className="text-[10px]" style={{ color: "#4A4A4A" }}>Date
                    <input type="date" value={r.date} onChange={(e) => setRecoltes((l) => l.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} className={`block ${inputCls}`} data-testid={`recolte-date-${i}`} /></label>
                  <label className="text-[10px]" style={{ color: "#4A4A4A" }}>Rendement (t/ha)
                    <input inputMode="decimal" value={r.rendement} onChange={(e) => setRecoltes((l) => l.map((x, j) => (j === i ? { ...x, rendement: e.target.value } : x)))} className={`block w-24 ${inputCls}`} data-testid={`recolte-rendement-${i}`} /></label>
                  <button type="button" onClick={() => setRecoltes((l) => l.filter((_, j) => j !== i))} aria-label="Retirer cette récolte"><X className="w-3.5 h-3.5" style={{ color: "#9A9A9A" }} /></button>
                </div>
              ))}
              <button type="button" onClick={() => setRecoltes((l) => [...l, { date: today(), rendement: "" }])} className="text-[10px] font-semibold underline" style={{ color: "#2D6A4F" }} data-testid="recolte-ajouter">Ajouter une récolte</button>
              <div className="flex gap-2">
                <Button size="sm" className="h-8 text-[11px] text-white" style={{ background: "#2D6A4F" }} disabled={busy} onClick={cloturer} data-testid="fiche-cloturer-ok">Clôturer le cycle</Button>
                <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => setPanel(null)}>Annuler</Button>
              </div>
            </div>
          )}
          {panel === "suivant" && (
            <div className="space-y-2" data-testid="fiche-suivant-form">
              <p className="text-[11px]" style={{ color: "#4A4A4A" }}>La nouvelle fiche reprend les objectifs et les apports prévus ; leurs dates sont décalées d'autant.</p>
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-[10px]" style={{ color: "#4A4A4A" }}>Début du cycle suivant
                  <input type="date" value={suivantDebut} onChange={(e) => setSuivantDebut(e.target.value)} className={`block ${inputCls}`} data-testid="suivant-debut" /></label>
                <Button size="sm" className="h-8 text-[11px] text-white" style={{ background: "#8B5E3C" }} disabled={busy || !suivantDebut} onClick={suivant} data-testid="fiche-suivant-ok">Créer la fiche</Button>
                <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => setPanel(null)}>Annuler</Button>
              </div>
            </div>
          )}
          {panel === "modele" && (
            <div className="space-y-2" data-testid="fiche-modele-form">
              <p className="text-[11px]" style={{ color: "#4A4A4A" }}>Le modèle garde le cycle, les objectifs et les apports prévus (doses par ha, dates en jours après le début du cycle). Il s'applique ensuite à un autre îlot ou à une autre parcelle.</p>
              <label className="block text-[10px]" style={{ color: "#4A4A4A" }}>Nom du modèle
                <input value={modeleForm.name} onChange={(e) => setModeleForm({ ...modeleForm, name: e.target.value })} className={`block w-full ${inputCls}`} data-testid="modele-nom" /></label>
              <label className="block text-[10px]" style={{ color: "#4A4A4A" }}>Partager avec (e-mails, facultatif) : ils pourront l'appliquer, pas le modifier
                <input value={modeleForm.shared} onChange={(e) => setModeleForm({ ...modeleForm, shared: e.target.value })} placeholder="producteur@exemple.re, …" className={`block w-full ${inputCls}`} data-testid="modele-partage" /></label>
              {isAdmin && (
                <label className="flex items-center gap-2 text-[11px]"><input type="checkbox" checked={modeleForm.template} onChange={(e) => setModeleForm({ ...modeleForm, template: e.target.checked })} /> Publier pour tous les comptes (modèle Véyèr CIEL)</label>
              )}
              <div className="flex gap-2">
                <Button size="sm" className="h-8 text-[11px] text-white" style={{ background: "#8B5E3C" }} disabled={busy || !modeleForm.name.trim()} onClick={enregistrerModele} data-testid="modele-enregistrer">Enregistrer le modèle</Button>
                <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => setPanel(null)}>Annuler</Button>
              </div>
            </div>
          )}
          {panel === "depuis-modele" && hasEnCours && (
            <p className="text-[11px]" style={{ color: "#9A6A12" }}>Une fiche est en cours : clôturez-la avant d'en créer une autre.</p>
          )}
        </div>
      )}

      {/* Téléphone : un onglet à la fois */}
      <div className="grid grid-cols-2 md:hidden border-b border-black/10">
        {[["bilan", "Bilan"], ["plan", `Plan d'apports${aFaire ? ` · ${aFaire} à faire` : ""}`]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setVue(v)} className="h-10 text-[12px]"
            style={vue === v ? { borderBottom: "2px solid #2D6A4F", fontWeight: 600, color: "#1A1A1A" } : { color: "#7A7A7A" }} data-testid={`fiche-vue-${v}`}>{l}</button>
        ))}
      </div>

      <div className="p-3 flex flex-wrap gap-5">
        {/* Bilan du cycle */}
        <section className={`${vue === "bilan" ? "" : "hidden"} md:block flex-[1_1_440px] min-w-0 space-y-1.5`} data-testid="fiche-bilan">
          <p className={`${sectionTitle} hidden md:block`} style={{ color: "#1A1A1A" }}>Bilan du cycle · kg/ha</p>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] tabular-nums border-collapse">
              <thead>
                <tr className="border-b" style={{ borderColor: "#1A1A1A", color: "#4A4A4A" }}>
                  <th className={th}>Élément</th>
                  <th className={`${th} text-right`}>Objectif</th>
                  <th className={`${th} text-right hidden sm:table-cell`}>Prévu</th>
                  <th className={`${th} text-right`}>Réalisé</th>
                  <th className={`${th} hidden sm:table-cell w-24`}>Avancement</th>
                  <th className={th}>Écart</th>
                </tr>
              </thead>
              <tbody>
                {(bilan?.elements || []).map((e) => {
                  const hasObj = e.objectif != null;
                  const pct = hasObj && e.objectif > 0 ? Math.min(100, (e.realise / e.objectif) * 100) : 0;
                  const reste = hasObj ? e.objectif - e.realise : null;
                  const ecart = hasObj ? (reste > 0 ? `reste ${fmtKg(reste)}` : reste < 0 ? `dépassé de ${fmtKg(-reste)}` : "atteint")
                    : (e.realise ? "pas d'objectif" : "non apporté");
                  return (
                    <tr key={e.code} className="border-b border-black/5" data-testid={`fiche-bilan-${e.code}`}>
                      <td className={`${td} font-semibold`} style={{ color: "#1A1A1A" }}>{(ELEMENTS.find((x) => x.code === e.code) || {}).label}</td>
                      <td className={`${td} text-right`} style={{ color: "#4A4A4A" }}>{hasObj ? fmtKg(e.objectif) : "—"}</td>
                      <td className={`${td} text-right hidden sm:table-cell`} style={{ color: "#7A7A7A" }}>{fmtKg(e.prevu)}</td>
                      <td className={`${td} text-right font-semibold`} style={{ color: e.realise ? "#1A1A1A" : "#C2410C" }}>{fmtKg(e.realise)}</td>
                      <td className={`${td} hidden sm:table-cell`}>
                        {hasObj && <div className="h-1.5 mt-1 rounded-full overflow-hidden" style={{ background: "#EEE9E2" }}><div className="h-full" style={{ width: `${pct}%`, background: e.realise > e.objectif * 1.1 ? "#C2410C" : "#2D6A4F" }} /></div>}
                      </td>
                      <td className={td} style={{ color: !hasObj ? (e.realise ? "#9A9A9A" : "#C2410C") : reste < 0 ? "#C2410C" : "#1A1A1A" }}>{ecart}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px]" style={{ color: "#7A7A7A" }}>Teneurs totales des produits · fertilisation et biostimulants saisis entre les dates du cycle.</p>
          {bilan?.non_comptes?.length > 0 && (
            <p className="text-[10px]" style={{ color: "#9A6A12" }}>
              {bilan.non_comptes.length} apport(s) du cycle non compté(s) : {bilan.non_comptes.map((n) => `${n.product_name} (${n.raison})`).join(" ; ")}
            </p>
          )}
        </section>

        {/* Plan d'apports et traçabilité */}
        <section className={`${vue === "plan" ? "" : "hidden"} md:block flex-[1_1_380px] min-w-0 space-y-1.5`} data-testid="fiche-plan">
          <p className={`${sectionTitle} hidden md:block`} style={{ color: "#1A1A1A" }}>Plan d'apports</p>
          {!prevusList.length && <p className="text-[11px]" style={{ color: "#7A7A7A" }}>Aucun apport prévu.</p>}
          {prevusList.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] border-collapse">
                <thead>
                  <tr className="border-b" style={{ borderColor: "#1A1A1A", color: "#4A4A4A" }}>
                    <th className={th}>Stade</th><th className={th}>Produit · dose</th><th className={`${th} hidden sm:table-cell`}>Prévu</th><th className={th}>État</th>
                  </tr>
                </thead>
                <tbody>
                  {prevusList.map((p) => {
                    const done = !!p.realise_fertilisation_id;
                    const u = units[p.prevu_id];
                    const resume = u?.units ? ELEMENTS.filter((e) => u.units[e.code]).slice(0, 4).map((e) => `${e.label} ${fmtKg(u.units[e.code])}`).join(" · ") : null;
                    return (
                      <Fragment key={p.prevu_id}>
                        <tr className="border-b border-black/5" style={{ background: done ? "#F3F8F5" : undefined }} data-testid={`fiche-prevu-${p.prevu_id}`}>
                          <td className={`${td} font-semibold`} style={{ color: "#1A1A1A" }}>{p.stade || "—"}</td>
                          <td className={td}>
                            <p style={{ color: "#1A1A1A" }}>{p.product_name}</p>
                            <p className="tabular-nums" style={{ color: "#7A7A7A" }}>{[p.dose && `${p.dose} ${p.unit || ""}`, resume && `${resume} /ha`].filter(Boolean).join(" · ")}</p>
                            {u && !u.units && <p className="text-[10px] italic" style={{ color: "#9A6A12" }}>Non compté : {u.raison}</p>}
                            {u && !u.units && <CompleterProduit info={u} onDone={() => load(fiche.fiche_id)} />}
                          </td>
                          <td className={`${td} tabular-nums hidden sm:table-cell`} style={{ color: "#4A4A4A" }}>{p.date_prevue ? frDate(p.date_prevue) : "—"}</td>
                          <td className={td}>
                            {done && <span className="inline-flex items-center gap-1 font-semibold" style={{ color: "#2D6A4F" }}><CheckCircle2 className="w-3 h-3" /> réalisé</span>}
                            {!done && !termine && realising?.prevu_id !== p.prevu_id && (
                              <Button size="sm" className="h-7 text-[10px] text-white" style={{ background: "#2D6A4F" }}
                                onClick={() => setRealising({ prevu_id: p.prevu_id, date: p.date_prevue && p.date_prevue <= today() ? p.date_prevue : today(), dose: p.dose || "" })}
                                data-testid={`fiche-realiser-${p.prevu_id}`}>Marquer réalisé</Button>
                            )}
                            {!done && termine && <span style={{ color: "#7A7A7A" }}>non fait</span>}
                          </td>
                        </tr>
                        {realising?.prevu_id === p.prevu_id && (
                          <tr className="border-b border-black/5" style={{ background: "#FAFAF7" }}>
                            <td colSpan={4} className="px-2 py-2">
                              <div className="flex flex-wrap items-end gap-2">
                                <label className="text-[10px]" style={{ color: "#4A4A4A" }}>Date
                                  <input type="date" value={realising.date} onChange={(e) => setRealising({ ...realising, date: e.target.value })} className={`block ${inputCls}`} data-testid="fiche-realiser-date" />
                                </label>
                                <label className="text-[10px]" style={{ color: "#4A4A4A" }}>Dose réelle ({p.unit})
                                  <input inputMode="decimal" value={realising.dose} onChange={(e) => setRealising({ ...realising, dose: e.target.value })} className={`block w-24 ${inputCls}`} data-testid="fiche-realiser-dose" />
                                </label>
                                <Button size="sm" className="h-8 text-[11px] text-white" style={{ background: "#2D6A4F" }} disabled={busy || !realising.date} onClick={realiser} data-testid="fiche-realiser-ok">
                                  {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Enregistrer l'apport
                                </Button>
                                <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => setRealising(null)}>Annuler</Button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!termine && (
            <button type="button" onClick={() => onEdit(fiche)} className="h-8 px-3 rounded-md border border-dashed text-[11px]" style={{ borderColor: "#7A7A7A", color: "#4A4A4A" }} data-testid="fiche-ajouter-apport">+ Ajouter un apport prévu</button>
          )}
          <p className={`${sectionTitle} pt-3`} style={{ color: "#1A1A1A" }}>Traçabilité</p>
          <p className="text-[10px] tabular-nums whitespace-pre-line" style={{ color: "#7A7A7A" }} data-testid="fiche-tracabilite">
            {[`Créée le ${frDate((fiche.created_at || "").slice(0, 10))} par ${fiche.created_by_name || "—"}`,
              fiche.updated_at && fiche.updated_at !== fiche.created_at ? `Modifiée le ${frDate(fiche.updated_at.slice(0, 10))} par ${fiche.updated_by_name || fiche.created_by_name || "—"}` : null,
              fiche.cloturee_at ? `Clôturée le ${frDate(fiche.cloturee_at.slice(0, 10))} par ${fiche.cloturee_by_name || "—"}` : null].filter(Boolean).join("\n")}
          </p>
        </section>
      </div>

      {(fiches.length > 1 || (!hasEnCours && termine && panel !== "depuis-modele")) && (
        <div className="px-3 pb-3 pt-2 border-t border-black/5 space-y-1.5">
          {fiches.length > 1 && (
            <div data-testid="fiche-historique">
              <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: "#7A7A7A" }}>Cycles</p>
              <div className="flex flex-wrap gap-1.5">
                {fiches.map((f) => (
                  <button key={f.fiche_id} type="button" onClick={() => load(f.fiche_id)}
                    className="px-2 py-1 rounded-md text-[10px] border" style={f.fiche_id === fiche.fiche_id ? { background: "#8B5E3C", color: "#FFF", borderColor: "#8B5E3C" } : { borderColor: "#0000001A", color: "#4A4A4A" }}>
                    {frDate(f.date_debut)}{f.date_fin ? ` → ${frDate(f.date_fin)}` : ""} · {f.statut === "en_cours" ? "en cours" : "clôturé"}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!hasEnCours && termine && panel !== "depuis-modele" && (
            <p className="text-[10px]" style={{ color: "#7A7A7A" }}>
              Ou créer la fiche suivante <button type="button" className="underline" style={{ color: "#8B5E3C" }} onClick={() => setPanel("depuis-modele")} data-testid="fiche-nouvelle-modele">depuis un modèle</button>
              {" "}ou <button type="button" className="underline" style={{ color: "#8B5E3C" }} onClick={() => onEdit({})} data-testid="fiche-nouvelle-vide">vide</button>.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const emptyPrevu = () => ({ product_name: "", category: "engrais", dose: "", unit: "kg/ha", date_prevue: "", stade: "", teneurs: {}, custom: "" });

/** Formulaire de la fiche : cycle, objectifs par élément, apports prévus. */
export function FicheFertiForm({ parcelId, ilotId, cultureType, refData, fiche, onSaved, onCancel }) {
  const isNew = !fiche?.fiche_id;
  const cultureOf = useCultureLookup();
  const [f, setF] = useState(() => {
    const culture = fiche?.culture ?? (cultureType || "");
    const profil = profilCulture(culture);
    return {
    type_cycle: fiche?.type_cycle || (profil === "canne" ? "campagne" : profil === "perenne" ? "entretien" : "cycle"),
    conduite: fiche?.conduite || (profil === "canne" ? "repousse" : null),
    culture,
    date_debut: fiche?.date_debut || today(),
    date_fin: fiche?.date_fin || "",
    recoltes_prevues: fiche?.recoltes_prevues ?? "",
    objectif_rendement_t_ha: fiche?.objectif_rendement_t_ha ?? "",
    sol_type: fiche?.sol_type || "",
    analyse_sol_id: fiche?.analyse_sol_id || "",
    precedent: fiche?.precedent || "",
    irrigation: fiche?.irrigation || "",
    objectifs_source: fiche?.objectifs_source || "",
    notes: fiche?.notes || "",
  };
  });
  // Fin de campagne proposée (un an) tant que l'utilisateur ne l'a pas changée
  const [finAuto, setFinAuto] = useState(isNew);
  const profil = profilCulture(f.culture, cultureOf(f.culture));
  // La culture décide du cycle : canne → campagne, pérenne → fond ou entretien, sinon cycle de culture
  const setCulture = (culture) => setF((prev) => {
    const pr = profilCulture(culture, cultureOf(culture));
    const type_cycle = pr === "canne" ? "campagne" : pr === "perenne" ? (["fond", "entretien"].includes(prev.type_cycle) ? prev.type_cycle : "entretien") : "cycle";
    const conduite = pr === "canne" ? (prev.conduite || "repousse") : null;
    return { ...prev, culture, type_cycle, conduite, date_fin: finAuto ? (type_cycle === "campagne" ? finCampagne(prev.date_debut) : "") : prev.date_fin };
  });
  const setDebut = (date_debut) => setF((prev) => ({ ...prev, date_debut, date_fin: finAuto && prev.type_cycle === "campagne" ? finCampagne(date_debut) : prev.date_fin }));
  useEffect(() => {
    if (isNew && f.type_cycle === "campagne" && !f.date_fin) setF((prev) => ({ ...prev, date_fin: finCampagne(prev.date_debut) }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const stades = STADES_CULTURE[`${normCulture(f.culture)}|${f.conduite}`] || STADES_CULTURE[normCulture(f.culture)]
    || STADES_CULTURE[normCulture(cultureOf(f.culture)?.libelle)] || STADES[f.type_cycle] || [];
  const [objectifs, setObjectifs] = useState(() => Object.fromEntries(Object.entries(fiche?.objectifs || {}).map(([k, v]) => [k, String(v)])));
  const [prevus, setPrevus] = useState(() => (fiche?.prevus || []).map((p) => ({
    ...p, dose: p.dose ?? "", date_prevue: p.date_prevue || "", stade: p.stade || "",
    teneurs: p.teneurs ? { ...Object.fromEntries(Object.entries(p.teneurs).filter(([k]) => k !== "oligo").map(([k, v]) => [k, String(v)])), oligo: Object.fromEntries(Object.entries(p.teneurs.oligo || {}).map(([k, v]) => [k, String(v)])) } : {},
    custom: "",
  })));
  const [recoltes, setRecoltes] = useState(() => (fiche?.recoltes || []).map((r) => ({ date: r.date, rendement: r.rendement_t_ha ?? "", notes: r.notes || "" })));
  const [analyses, setAnalyses] = useState([]);
  const [mesProduits, setMesProduits] = useState([]);
  const [solParcelle, setSolParcelle] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    authFetch(`${API_BASE}/carnet/analyses-sol?parcel_id=${parcelId}${ilotId ? `&ilot_id=${ilotId}` : ""}`).then((r) => (r.ok ? r.json() : [])).then(setAnalyses).catch(() => {});
    authFetch(`${API_BASE}/carnet/mes-produits`).then((r) => (r.ok ? r.json() : [])).then(setMesProduits).catch(() => {});
    // Type de sol déjà connu de la parcelle : pré-rempli pour une nouvelle fiche, modifiable
    authFetch(`${API_BASE}/parcels/${parcelId}`).then((r) => (r.ok ? r.json() : null)).then((p) => {
      const ts = p?.agronomic_data?.type_sol;
      const v = (ts && typeof ts === "object" ? ts.valeur : ts) || null;
      if (!v) return;
      setSolParcelle({ valeur: v, source: ts?.source || "" });
      setF((prev) => (prev.sol_type ? prev : { ...prev, sol_type: v }));
    }).catch(() => {});
  }, [parcelId, ilotId]);

  const productsFor = useMemo(() => {
    const cache = {};
    return (category) => (cache[category] ||= [
      ...mesProduits.filter((p) => p.category === category).map((p) => ({ ...p, mine: true })),
      ...fertiProducts(refData, category),
    ]);
  }, [refData, mesProduits]);

  const setPrevu = (i, patch) => setPrevus((list) => list.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const choose = (i, category, name) => {
    const prod = productsFor(category).find((x) => x.name === name);
    setPrevu(i, { product_name: name === "Autre" ? "" : name, custom: name === "Autre" ? (prevus[i].custom || "") : "", unit: prod?.unit || prevus[i].unit || "kg/ha", teneurs: {} });
  };

  const save = async () => {
    if (!f.date_debut) { toast.error("Indiquez la date de début du cycle"); return; }
    const items = prevus.map((p) => ({ ...p, product_name: (p.product_name || p.custom || "").trim() }));
    if (items.some((p) => !p.product_name)) { toast.error("Chaque apport prévu doit avoir un produit"); return; }
    setSaving(true);
    try {
      const known = (p) => productsFor(p.category).some((x) => x.name === p.product_name && !x.mine);
      const body = {
        parcel_id: parcelId, ilot_id: ilotId || null, ...f,
        date_fin: f.date_fin || null, analyse_sol_id: f.analyse_sol_id || null,
        recoltes_prevues: f.recoltes_prevues === "" ? null : parseInt(f.recoltes_prevues, 10),
        objectif_rendement_t_ha: f.objectif_rendement_t_ha === "" ? null : parseFloat(String(f.objectif_rendement_t_ha).replace(",", ".")),
        objectifs,
        recoltes: recoltes.filter((r) => r.date).map((r) => ({ date: r.date, rendement_t_ha: r.rendement === "" ? null : parseFloat(String(r.rendement).replace(",", ".")), notes: r.notes || null })),
        prevus: items.map((p) => ({
          prevu_id: p.prevu_id || null, product_name: p.product_name, category: p.category, dose: p.dose || null, unit: p.unit,
          date_prevue: p.date_prevue || null, stade: p.stade || null, teneurs: known(p) ? null : p.teneurs,
        })),
      };
      const res = await authFetch(`${API_BASE}/carnet/fiches-ferti${isNew ? "" : `/${fiche.fiche_id}`}`, {
        method: isNew ? "POST" : "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      await jsonOrThrow(res, "Enregistrement impossible");
      toast.success(isNew ? "Fiche ferti créée" : "Fiche ferti enregistrée");
      onSaved();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const remove = async () => {
    try {
      await jsonOrThrow(await authFetch(`${API_BASE}/carnet/fiches-ferti/${fiche.fiche_id}`, { method: "DELETE" }), "Suppression impossible");
      toast.success("Fiche supprimée (les apports réalisés restent dans le carnet)");
      onSaved();
    } catch (e) { toast.error(e.message); }
  };
  const [confirmDelete, setConfirmDelete] = useState(false);
  const lbl = "text-[11px] uppercase tracking-wider font-medium";

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4" data-testid="fiche-form">
        <div className="flex flex-wrap items-center gap-2 pb-2 border-b-2" style={{ borderColor: "#1A1A1A" }}>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}><ArrowLeft className="w-4 h-4" /></Button>
          <div className="flex-1 min-w-[160px]">
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#8B5E3C" }}>{isNew ? "Nouvelle fiche de fertilisation" : "Modifier la fiche de fertilisation"}</p>
            <h3 className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{ilotId ? "Îlot" : "Parcelle entière"}</h3>
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
        <div className="flex-[1_1_360px] min-w-0 space-y-4">
        <section className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wider pb-1 border-b" style={{ color: "#1A1A1A", borderColor: "#1A1A1A" }}>1 · Culture et cycle</p>
          <CulturePicker value={f.culture} onChange={setCulture} region={regionOfParcel(parcelId)} testId="fiche-culture" />
          {cultureType && normCulture(f.culture) === normCulture(cultureType) && (
            <p className="text-[10px]" style={{ color: "#7A7A7A" }}>Reprise {ilotId ? "de l'îlot" : "de la parcelle"}, modifiable.</p>
          )}
          <div className="rounded-lg p-2.5 space-y-2" style={{ background: "#F6EFE8" }} data-testid="fiche-cycle-deduit">
            {profil === "canne" && (
              <>
                <p className="text-xs" style={{ color: "#1A1A1A" }}><b>Campagne de coupe à coupe.</b> Plantation ou repousse ?</p>
                <div className="grid grid-cols-2 gap-2">
                  {[["plantation", "Plantation"], ["repousse", "Repousse"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => set("conduite", v)} className="h-9 rounded-md border text-xs"
                      style={f.conduite === v ? { borderColor: "#8B5E3C", background: "#FFFFFF", fontWeight: 600 } : { borderColor: "#0000001A", background: "#FFFFFF80" }}
                      data-testid={`fiche-conduite-${v}`}>{l}</button>
                  ))}
                </div>
              </>
            )}
            {profil === "perenne" && (
              <>
                <p className="text-xs" style={{ color: "#1A1A1A" }}><b>Culture pérenne.</b> Quel type d'apports pour ce cycle ?</p>
                <div className="grid grid-cols-2 gap-2">
                  {[["fond", "Fumure de fond", "tous les 3 ou 4 ans"], ["entretien", "Entretien", "entre deux fumures de fond"]].map(([v, l, h]) => (
                    <button key={v} type="button" onClick={() => set("type_cycle", v)} className="rounded-md border p-2 text-left"
                      style={f.type_cycle === v ? { borderColor: "#8B5E3C", background: "#FFFFFF" } : { borderColor: "#0000001A", background: "#FFFFFF80" }}
                      data-testid={`fiche-type-${v}`}>
                      <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>{l}</p><p className="text-[10px]" style={{ color: "#7A7A7A" }}>{h}</p>
                    </button>
                  ))}
                </div>
              </>
            )}
            {profil === "cycle" && (
              <p className="text-xs" style={{ color: "#1A1A1A" }}><b>Cycle de culture</b> : de la préparation à la dernière récolte. Indiquez le nombre de récoltes prévues si elles sont plusieurs.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={profil === "canne" ? "Début de la campagne" : "Début du cycle"} type="date" value={f.date_debut} onChange={setDebut} required testId="fiche-debut" />
            <Field label="Fin prévue" type="date" value={f.date_fin} onChange={(v) => { setFinAuto(false); set("date_fin", v); }} testId="fiche-fin" />
            {profil === "cycle" && <Field label="Récoltes prévues" type="number" value={f.recoltes_prevues} onChange={(v) => set("recoltes_prevues", v)} testId="fiche-recoltes" />}
            <Field label="Objectif de rendement (t/ha)" type="number" value={f.objectif_rendement_t_ha} onChange={(v) => set("objectif_rendement_t_ha", v)} testId="fiche-rendement" />
            <Field label="Précédent cultural" value={f.precedent} onChange={(v) => set("precedent", v)} testId="fiche-precedent" />
            <label className="block">
              <span className={lbl} style={{ color: "#4A4A4A" }}>Type de sol</span>
              <input list="fiche-sols" value={f.sol_type} onChange={(e) => set("sol_type", e.target.value)} placeholder="Choisir ou saisir…"
                className="w-full h-9 mt-1 px-2 rounded-md border border-black/10 bg-white text-sm" data-testid="fiche-sol" />
              <datalist id="fiche-sols">{SOLS.map((s) => <option key={s} value={s} />)}</datalist>
              {solParcelle && f.sol_type === solParcelle.valeur && (
                <span className="block text-[10px] mt-0.5" style={{ color: "#7A7A7A" }} data-testid="fiche-sol-source">Repris de la parcelle{solParcelle.source ? ` (${solParcelle.source})` : ""}, modifiable</span>
              )}
            </label>
            <Field label="Irrigation" value={f.irrigation} onChange={(v) => set("irrigation", v)} placeholder="Aucune, goutte-à-goutte…" testId="fiche-irrigation" />
          </div>
          <label className="block">
            <span className={lbl} style={{ color: "#4A4A4A" }}>Analyse de sol</span>
            <select value={f.analyse_sol_id} onChange={(e) => set("analyse_sol_id", e.target.value)} className="w-full h-9 mt-1 px-2 rounded-md border border-black/10 bg-white text-sm" data-testid="fiche-analyse">
              <option value="">{analyses.length ? "Choisir une analyse du carnet" : "Aucune analyse dans le carnet"}</option>
              {analyses.map((a) => <option key={a.analyse_id} value={a.analyse_id}>{frDate(a.date_prelevement)}{a.ph_eau ? ` · pH ${a.ph_eau}` : ""}{a.matiere_organique ? ` · MO ${a.matiere_organique} %` : ""}</option>)}
            </select>
          </label>
        </section>
        </div>

        <div className="flex-[1_1_460px] min-w-0 space-y-4">
        <section className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wider pb-1 border-b" style={{ color: "#1A1A1A", borderColor: "#1A1A1A" }}>2 · Objectifs du cycle · kg/ha</p>
          <p className="text-[10px]" style={{ color: "#7A7A7A" }}>Besoins à couvrir sur le cycle, par exemple repris d'un plan AMENDA. Laissez vide un élément sans objectif.</p>
          <div className="grid grid-cols-4 sm:grid-cols-6 xl:grid-cols-12 gap-1.5">
            {ELEMENTS.map((e) => (
              <label key={e.code} className="block">
                <span className="text-[10px] font-semibold" style={{ color: "#4A4A4A" }}>{e.label}</span>
                <input inputMode="decimal" value={objectifs[e.code] ?? ""} onChange={(ev) => setObjectifs((o) => ({ ...o, [e.code]: ev.target.value }))}
                  className="w-full h-8 px-2 mt-0.5 rounded-md border border-black/10 bg-white text-sm tabular-nums" data-testid={`fiche-obj-${e.code}`} />
              </label>
            ))}
          </div>
          <Field label="Source des objectifs" value={f.objectifs_source} onChange={(v) => set("objectifs_source", v)} placeholder="Ex. plan AMENDA du 12/10/2026, conseil du technicien…" testId="fiche-source" />
        </section>

        <section className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wider pb-1 border-b" style={{ color: "#1A1A1A", borderColor: "#1A1A1A" }}>3 · Plan d'apports · par ha</p>
          <datalist id="fiche-stades">{stades.map((s) => <option key={s} value={s} />)}</datalist>
          {prevus.map((p, i) => {
            const done = !!p.realise_fertilisation_id;
            const prods = productsFor(p.category);
            const sel = prods.find((x) => x.name === p.product_name);
            const showTeneurs = !done && (!sel || sel.mine) && (p.custom || (p.product_name && !sel));
            return (
              <div key={p.prevu_id || i} className="rounded-lg border border-black/10 p-2.5 space-y-2" data-testid={`fiche-prevu-form-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold" style={{ color: "#8B5E3C" }}>Apport {i + 1}</span>
                  {done && <span className="text-[10px] font-semibold" style={{ color: "#2D6A4F" }}>· réalisé</span>}
                  <span className="flex-1" />
                  {!done && <button type="button" onClick={() => setPrevus((l) => l.filter((_, j) => j !== i))} aria-label="Retirer cet apport prévu"><X className="w-3.5 h-3.5" style={{ color: "#9A9A9A" }} /></button>}
                </div>
                <div className="flex gap-1.5">
                  {[["engrais", "Engrais"], ["amendement", "Amendement"]].map(([v, l]) => (
                    <button key={v} type="button" disabled={done} onClick={() => setPrevu(i, { category: v, product_name: "", custom: "", teneurs: {} })}
                      className="flex-1 h-7 rounded-md text-[11px] border" style={p.category === v ? { background: "#8B5E3C", color: "#FFF", borderColor: "#8B5E3C" } : { borderColor: "#0000001A" }}>{l}</button>
                  ))}
                </div>
                {done ? <p className="text-xs font-semibold">{p.product_name}</p> : (
                  <>
                    {/* Un produit enregistré hors catalogue (saisi librement) s'affiche dans la case du nom libre */}
                    <SearchableSelect items={productItems(prods)} value={sel ? p.product_name : (p.custom || p.product_name ? "Autre" : "")} onSelect={(v) => choose(i, p.category, v)}
                      placeholder="Rechercher un produit..." searchPlaceholder="Nom du produit ou n° AMM..." testId={`fiche-prevu-produit-${i}`} className="text-sm" />
                    {!sel && (
                      <input value={p.custom || p.product_name} onChange={(e) => setPrevu(i, { custom: e.target.value, product_name: "" })} placeholder="Produit absent de la liste : son nom"
                        className="w-full h-8 px-2 rounded-md border border-black/10 bg-white text-sm" data-testid={`fiche-prevu-libre-${i}`} />
                    )}
                  </>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <label className="block"><span className="text-[10px] font-semibold" style={{ color: "#4A4A4A" }}>Dose</span>
                    <input inputMode="decimal" value={p.dose} disabled={done} onChange={(e) => setPrevu(i, { dose: e.target.value })} className="w-full h-8 px-2 mt-0.5 rounded-md border border-black/10 bg-white text-sm" data-testid={`fiche-prevu-dose-${i}`} /></label>
                  <label className="block"><span className="text-[10px] font-semibold" style={{ color: "#4A4A4A" }}>Unité</span>
                    <select value={p.unit} disabled={done} onChange={(e) => setPrevu(i, { unit: e.target.value })} className="w-full h-8 px-1 mt-0.5 rounded-md border border-black/10 bg-white text-sm">
                      {[...new Set([...UNITS, p.unit].filter(Boolean))].map((u) => <option key={u} value={u}>{u}</option>)}
                    </select></label>
                  <label className="block"><span className="text-[10px] font-semibold" style={{ color: "#4A4A4A" }}>Date prévue</span>
                    <input type="date" value={p.date_prevue} disabled={done} onChange={(e) => setPrevu(i, { date_prevue: e.target.value })} className="w-full h-8 px-2 mt-0.5 rounded-md border border-black/10 bg-white text-sm" /></label>
                  <label className="block"><span className="text-[10px] font-semibold" style={{ color: "#4A4A4A" }}>Stade</span>
                    <input list="fiche-stades" value={p.stade} disabled={done} onChange={(e) => setPrevu(i, { stade: e.target.value })} className="w-full h-8 px-2 mt-0.5 rounded-md border border-black/10 bg-white text-sm" data-testid={`fiche-prevu-stade-${i}`} /></label>
                </div>
                {showTeneurs && <TeneursFields teneurs={p.teneurs || {}} onChange={(t) => setPrevu(i, { teneurs: t })} />}
              </div>
            );
          })}
          <Button type="button" variant="outline" size="sm" className="w-full h-8 text-[11px] gap-1" onClick={() => setPrevus((l) => [...l, emptyPrevu()])} data-testid="fiche-ajouter-prevu">
            <Plus className="w-3.5 h-3.5" /> Ajouter un apport prévu
          </Button>
        </section>
        </div>
        </div>

        {(!isNew || recoltes.length > 0) && (
          <section className="space-y-2">
            <p className="text-[11px] font-bold uppercase tracking-wider pb-1 border-b" style={{ color: "#1A1A1A", borderColor: "#1A1A1A" }}>4 · Récoltes</p>
            {recoltes.map((r, i) => (
              <div key={i} className="flex flex-wrap items-end gap-2">
                <Field label="Date" type="date" value={r.date} onChange={(v) => setRecoltes((l) => l.map((x, j) => (j === i ? { ...x, date: v } : x)))} testId={`fiche-recolte-date-${i}`} />
                <Field label="Rendement (t/ha)" type="number" value={r.rendement} onChange={(v) => setRecoltes((l) => l.map((x, j) => (j === i ? { ...x, rendement: v } : x)))} testId={`fiche-recolte-rdt-${i}`} />
                <button type="button" className="mb-2" onClick={() => setRecoltes((l) => l.filter((_, j) => j !== i))} aria-label="Retirer cette récolte"><X className="w-3.5 h-3.5" style={{ color: "#9A9A9A" }} /></button>
              </div>
            ))}
            <button type="button" onClick={() => setRecoltes((l) => [...l, { date: today(), rendement: "", notes: "" }])} className="text-[11px] font-semibold underline" style={{ color: "#2D6A4F" }}>Ajouter une récolte</button>
          </section>
        )}

        <div>
          <Label className={lbl} style={{ color: "#4A4A4A" }}>Notes</Label>
          <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} className="text-sm bg-white border-black/10 min-h-[50px] mt-1" data-testid="fiche-notes" />
        </div>

        <Button onClick={save} disabled={saving} className="w-full h-10 text-sm font-semibold rounded-xl text-white gap-2" style={{ background: "#8B5E3C" }} data-testid="fiche-enregistrer">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />} {isNew ? "Créer la fiche" : "Enregistrer la fiche"}
        </Button>
        {!isNew && (confirmDelete ? (
          <div className="flex items-center gap-2 text-[11px]">
            <span style={{ color: "#DC2626" }}>Supprimer la fiche ? Les apports déjà réalisés restent dans le carnet.</span>
            <Button size="sm" variant="outline" className="h-7 text-[10px] border-red-200 text-red-600" onClick={remove} data-testid="fiche-supprimer-ok">Supprimer</Button>
            <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setConfirmDelete(false)}>Annuler</Button>
          </div>
        ) : (
          <button type="button" className="flex items-center gap-1 text-[11px]" style={{ color: "#DC2626" }} onClick={() => setConfirmDelete(true)} data-testid="fiche-supprimer"><Trash2 className="w-3.5 h-3.5" /> Supprimer la fiche</button>
        ))}
      </div>
    </ScrollArea>
  );
}
